import React, { useEffect, useState } from 'react'
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridSelectionModel,
} from '@mui/x-data-grid'
import { DatagridPlaceholder } from 'components'
import { useTranslation } from 'react-i18next'
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
} from '@mui/material'
import { Close } from '@mui/icons-material'

interface ParticipantGridProps {
  rows: GridRowModel[]
  columns: GridColDef[]
  selectedRowsIds: number[]
  setSelectedRowsIds: (ids: number[]) => void
  placeholderText: string
  hidden?: boolean
  loading?: boolean
}

const SHARED_INPUT_STYLES = {
  height: '38px',
  border: '1px solid rgba(224, 224, 224, 1)',
  backgroundColor: (theme: Theme) => theme.palette.background.default,
  borderRadius: 0,
}

const OUTLINE_STYLES = {
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
}

const LABEL_STYLES = {
  fontWeight: 'bold',
}

const ParticipantGrid: React.FC<ParticipantGridProps> = ({
  rows,
  columns,
  selectedRowsIds,
  setSelectedRowsIds,
  placeholderText,
  hidden = false,
  loading = false,
}) => {
  if (hidden) return null

  const { t } = useTranslation()
  const PAGE_SIZE = 10
  const hideFooter = rows.length < PAGE_SIZE

  const [licenseFilter, setLicenseFilter] = useState<'assigned' | 'unassigned' | ''>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredRows, setFilteredRows] = useState(rows)

  useEffect(() => {
    let result = rows

    if (licenseFilter) {
      result = result.filter((row) =>
        licenseFilter === 'assigned' ? row.hasLicense : !row.hasLicense
      )
    }

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase()
      result = result.filter((row) => row.name?.toLowerCase().includes(lower))
    }

    setFilteredRows(result)
  }, [licenseFilter, searchTerm, rows])

  // Component for loading or empty
  const NoRowsOverlay = () =>
    loading ? (
      <Box
        display='flex'
        height={'100%'}
        justifyContent='center'
        alignItems='center'
      >
        <CircularProgress size={20} />
      </Box>
    ) : (
      <DatagridPlaceholder title={placeholderText} />
    )

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Box display='flex' gap={2}>
        <TextField
          size='small'
          fullWidth
          label={t('search_by_name')}
          variant='outlined'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchTerm && (
              <IconButton
                size='small'
                onClick={() => setSearchTerm('')}
                aria-label={t('clear_search')}
              >
                <Close fontSize='small' />
              </IconButton>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              '& input': {
                paddingInline: '1.2rem',
                height: '100%',
              },
              ...SHARED_INPUT_STYLES,
              ...OUTLINE_STYLES,
            },
            '& .MuiInputLabel-root': LABEL_STYLES,
          }}
        />

        <FormControl
          fullWidth
          size='small'
          sx={{ borderRadius: 0, maxHeight: '36px' }}
        >
          <InputLabel sx={LABEL_STYLES} id='license-filter-label'>
            {t('filter_by_license')}
          </InputLabel>
          <Select
            labelId='license-filter-label'
            value={licenseFilter}
            label={t('filter_by_license')}
            onChange={(e: SelectChangeEvent<'assigned' | 'unassigned' | ''>) =>
              setLicenseFilter(e.target.value as 'assigned' | 'unassigned' | '')
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              margin: '0',
              ...SHARED_INPUT_STYLES,
              ...OUTLINE_STYLES,
            }}
          >
            <MenuItem value=''>{t('all')}</MenuItem>
            <MenuItem value='assigned'>{t('assigned')}</MenuItem>
            <MenuItem value='unassigned'>{t('unassigned')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DataGrid
        autoHeight
        disableSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        checkboxSelection
        rowHeight={66}
        density='standard'
        columns={columns}
        pageSize={PAGE_SIZE}
        rows={filteredRows}
        rowsPerPageOptions={[10, 25, 50]}
        hideFooterPagination={hideFooter}
        selectionModel={selectedRowsIds}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        onSelectionModelChange={(ids: GridSelectionModel) =>
          setSelectedRowsIds(ids as number[])
        }
        components={{
          NoRowsOverlay,
        }}
        sx={{
          borderRadius: 0,
          backgroundColor: '#fff',
          '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fff' },
          '& .MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          '& .MuiDataGrid-iconSeparator': { display: 'none' },
          '& .MuiDataGrid-menuIcon': {
            display: 'none !important',
          },
          ...(hideFooter && {
            '& .MuiDataGrid-footerContainer': { display: 'none' },
          }),
        }}
      />
    </Box>
  )
}

export default ParticipantGrid
